
import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'

import { useLessonEditStore } from './edit/store'
import { useLessonDestroyApi } from '../../api/lessons/destroy.hook'
import { useLessonPrivileges } from './lesson-privileges.hook'
import { useDisciplineRefresh } from '../discipline-refresh.hook'

interface LessonEditDeleteReturn {
  refCanDelete: ComputedRef<boolean>
  refIsConfirmOpen: ComputedRef<boolean>
  refIsDeleting: Ref<boolean>
  refDetail: ComputedRef<string>
  openDelete: () => void
  closeDelete: () => void
  confirmDelete: () => Promise<void>
}

/** Deleting the lesson from its own page, as the row menu of the Lessons tab does: a confirmation, the delete, and
 *  the way back to the tab, whose balance counters change with every lesson. */
export const useLessonEditDelete = (): LessonEditDeleteReturn => {
  const route = useRoute()
  const store = useLessonEditStore()
  const { canDeleteBookings } = useLessonPrivileges()
  const { refreshDiscipline } = useDisciplineRefresh()

  const disciplineId = route.params.id as string

  const refCanDelete = computed<boolean>(() => canDeleteBookings.value && !!store.getLesson)

  const refIsConfirmOpen = computed<boolean>(() => store.isDeleteConfirmOpen)
  const refIsDeleting = ref<boolean>(false)

  const refDetail = computed<string>(() =>
    `Are you sure you want to delete lesson #${store.getLesson?.id ?? ''}? All of data will be permanently removed. This action cannot be undone.`)

  const openDelete = (): void => {
    if (!refCanDelete.value || store.isSaving) return

    store.setDeleteConfirmOpen(true)
  }

  const closeDelete = (): void => {
    if (refIsDeleting.value) return

    store.setDeleteConfirmOpen(false)
  }

  const confirmDelete = async (): Promise<void> => {
    const lesson = store.getLesson
    if (!lesson || refIsDeleting.value) return

    const isDeleted = await useLessonDestroyApi({
      disciplineId,
      lessonId: lesson.id,
      onStart: () => { refIsDeleting.value = true },
      onFinish: () => { refIsDeleting.value = false },
    })

    if (!isDeleted) {
      store.setDeleteConfirmOpen(false)
      return
    }

    // The discipline is reread once the Lessons tab is up: its page loader covers that read, whereas here it would
    // rise over the open modal.
    await navigateTo(resolveUrl(AppRoute.DisciplinesLessons, { id: disciplineId }) ?? '/')
    void refreshDiscipline()
  }

  return {
    refCanDelete,
    refIsConfirmOpen,
    refIsDeleting,
    refDetail,
    openDelete,
    closeDelete,
    confirmDelete,
  }
}
